import React from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      // 全ページを表示
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // ページ数が多い場合
      if (currentPage <= 3) {
        // 最初の方のページ
        for (let i = 1; i <= maxVisible; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // 最後の方のページ
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // 中間のページ
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: "8px",
        alignItems: "center",
        justifyContent: "center",
        mt: 3,
        mb: 3,
      }}
    >
      {/* 左矢印 */}
      <IconButton
        onClick={handlePrevious}
        disabled={currentPage === 1}
        sx={{
          width: 32,
          height: 32,
          borderRadius: "6px",
          backgroundColor: "#f5f5f5",
          padding: "6px",
          "&:hover": {
            backgroundColor: "#e5e5e5",
          },
          "&:disabled": {
            backgroundColor: "#f5f5f5",
            opacity: 0.5,
          },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 14, color: "rgba(0,0,0,0.88)" }} />
      </IconButton>

      {/* ページ番号 */}
      {getPageNumbers().map((page, index) => {
        if (page === "...") {
          return (
            <Box
              key={`ellipsis-${index}`}
              sx={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                color: "rgba(0,0,0,0.88)",
              }}
            >
              •••
            </Box>
          );
        }

        const isActive = page === currentPage;

        return (
          <Box
            key={page}
            onClick={() => onPageChange(page as number)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              backgroundColor: isActive ? "#0b9dbd" : "#f5f5f5",
              border: isActive ? "1px solid #0b9dbd" : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: isActive ? "#0a8aa7" : "#e5e5e5",
              },
            }}
          >
            <Box
              component="p"
              sx={{
                margin: 0,
                fontFamily: '"Noto Sans JP", sans-serif',
                fontSize: "14px",
                lineHeight: "22px",
                fontWeight: isActive ? 500 : 400,
                color: isActive ? "#ffffff" : "rgba(0,0,0,0.88)",
                textAlign: "center",
              }}
            >
              {page}
            </Box>
          </Box>
        );
      })}

      {/* 右矢印 */}
      <IconButton
        onClick={handleNext}
        disabled={currentPage === totalPages}
        sx={{
          width: 32,
          height: 32,
          borderRadius: "6px",
          backgroundColor: "#f5f5f5",
          padding: "6px",
          "&:hover": {
            backgroundColor: "#e5e5e5",
          },
          "&:disabled": {
            backgroundColor: "#f5f5f5",
            opacity: 0.5,
          },
        }}
      >
        <ChevronRightIcon sx={{ fontSize: 14, color: "rgba(0,0,0,0.88)" }} />
      </IconButton>
    </Box>
  );
};
